import { PersistedMetamodel } from '@ardoq/api-types';
import { reducer, streamReducer } from '@ardoq/rxbeach';
import { SimpleBlockDiagramContentType } from '@ardoq/simple-block-diagram/types';
import type {
  MetamodelContext,
  MetamodelContextSettingMap,
  MetamodelEntityContext,
  SelectedMetamodelItem,
} from 'metamodel/types';
import { updateMetamodel } from 'streams/metamodels/metamodelActions';
import {
  selectMetamodelForSlide,
  selectMetamodelItem,
  setMetamodelContext,
  setMetamodelContextSettings,
  setMetamodelInfoContainerIsOpen,
} from './actions';
import { metamodelActualNavigation$ } from './actualMetamodelNavigation$';
import {
  InfoContainerWidth,
  MetamodelActualNavigationState,
  MetamodelNavigationState,
  MetamodelPane,
} from './types';
import {
  getContainerWidthFromMetamodelContext,
  getContextFromMetamodelItem,
} from './utils';

export const EMPTY_METAMODEL_CONTEXT: MetamodelContext = {
  id: '',
  name: '',
  type: SimpleBlockDiagramContentType.INVALID,
  settings: { isolatedNavigation: false },
};
export const defaultState: MetamodelNavigationState = {
  pane: MetamodelPane.LIST,
  metamodelId: undefined,
  metamodel: undefined,
  context: EMPTY_METAMODEL_CONTEXT,
  infoContainerIsOpen: false,
  infoContainerWidth: InfoContainerWidth.NARROW,
};

const handleUpdateMetamodel = (
  currentState: MetamodelNavigationState,
  metamodel: PersistedMetamodel
): MetamodelNavigationState => {
  if (currentState.metamodelId !== metamodel._id) return currentState;

  return {
    ...currentState,
    metamodel,
  };
};

const handleSelectMetamodelForSlide = (
  currentState: MetamodelNavigationState,
  metamodel: PersistedMetamodel
): MetamodelNavigationState => ({
  ...defaultState,
  pane: MetamodelPane.SELECTED,
  metamodelId: metamodel._id,
  metamodel,
  context: {
    ...EMPTY_METAMODEL_CONTEXT,
    settings: currentState.context.settings,
  },
});

const handleSelectMetamodelItem = (
  currentState: MetamodelNavigationState,
  selectedItem: SelectedMetamodelItem
) => {
  const { metamodel } = currentState;
  if (!metamodel) return currentState;
  const context =
    getContextFromMetamodelItem(metamodel, selectedItem) ??
    EMPTY_METAMODEL_CONTEXT;
  const newContextWithSettings = {
    ...context,
    settings: currentState.context.settings,
  };
  return {
    ...currentState,
    context: newContextWithSettings,
    infoContainerIsOpen: true,
    infoContainerWidth: InfoContainerWidth.NARROW,
  };
};

const handleSetMetamodelContext = (
  currentState: MetamodelNavigationState,
  context: MetamodelEntityContext
) => {
  const newContextWithSettings = {
    ...context,
    settings: currentState.context.settings,
  };
  return {
    ...currentState,
    context: newContextWithSettings,
    infoContainerIsOpen: true,
    infoContainerWidth: getContainerWidthFromMetamodelContext(
      newContextWithSettings
    ),
  };
};

const handleSetMetamodelContextSettings = (
  currentState: MetamodelNavigationState,
  settings: MetamodelContextSettingMap
) => ({
  ...currentState,
  context: {
    ...currentState.context,
    settings,
  },
});

const handleSetMetamodelInfoContainerIsOpen = (
  currentState: MetamodelNavigationState,
  infoContainerIsOpen: boolean
) => ({
  ...currentState,
  infoContainerIsOpen,
});

const handleActualNavigation = (
  state: MetamodelNavigationState,
  { pane, metamodelId }: MetamodelActualNavigationState
): MetamodelNavigationState => {
  if (state.metamodelId !== metamodelId) {
    return {
      ...defaultState,
      pane,
      metamodelId,
      context: { ...EMPTY_METAMODEL_CONTEXT, settings: state.context.settings },
    };
  }

  return {
    ...state,
    pane,
  };
};

export const reducers = [
  streamReducer(metamodelActualNavigation$, handleActualNavigation),
  reducer(updateMetamodel, handleUpdateMetamodel),
  reducer(selectMetamodelForSlide, handleSelectMetamodelForSlide),
  reducer(selectMetamodelItem, handleSelectMetamodelItem),
  reducer(setMetamodelContext, handleSetMetamodelContext),
  reducer(setMetamodelContextSettings, handleSetMetamodelContextSettings),
  reducer(
    setMetamodelInfoContainerIsOpen,
    handleSetMetamodelInfoContainerIsOpen
  ),
];
